
import { JSX, useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { graphqlRequest } from 'lib/helpers'
import { sortByName } from 'lib/elementOrder'
import { MerchOverlaySummary } from 'types/api'
import listMyMerchOverlaysQuery from 'graphql/listMyMerchOverlays.graphql'

interface BoardChannel {
  merchOverlayId: string
  name: string
  channel: { login: string, channelId: string } | null
}

const toBoardChannel = (overlay: MerchOverlaySummary): BoardChannel => {
  const login = typeof overlay.extensionChannelLogin === 'string' ? overlay.extensionChannelLogin : ''
  const channelId = typeof overlay.extensionChannelId === 'string' ? overlay.extensionChannelId : ''
  return {
    merchOverlayId: overlay.merchOverlayId,
    name: overlay.name,
    channel: login !== '' && channelId !== '' ? { login, channelId } : null
  }
}

export const useMerchExtensionChannels = (auth0Token: string): {
  boards: BoardChannel[] | null
  loading: boolean
  loadError: string | null
  load: () => Promise<void>
} => {
  const [boards, setBoards] = useState<BoardChannel[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setLoadError(null)
    try {
      const { overlays } = await graphqlRequest<{ overlays: MerchOverlaySummary[] }>(
        listMyMerchOverlaysQuery, {}, auth0Token
      )
      setBoards(overlays.filter((o) => o.displayStyle !== 'TICKER').map(toBoardChannel))
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to read the merch boards')
    } finally {
      setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void load()
  }, [load])

  return { boards, loading, loadError, load }
}

const ChannelsBody = ({
  boards,
  loadError,
  onRetry
}: {
  boards: BoardChannel[] | null
  loadError: string | null
  onRetry: () => void
}): JSX.Element => {
  if (loadError !== null) {
    return (
      <div
        data-testid='merch-extension-channels-load-error'
        className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'
      >
        <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
        <span className='flex-1'>{loadError}</span>
        <button
          onClick={onRetry}
          className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
        >
          Retry
        </button>
      </div>
    )
  }
  if (boards === null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (boards.length === 0) {
    return (
      <p data-testid='merch-extension-channels-empty' className='mt-3 text-xs text-dm-text-secondary'>
        No merch boards on this account. Tickers are never listed here,
        they have no Twitch extension surface.
      </p>
    )
  }
  return (
    <ul className='mt-3 space-y-1.5' data-testid='merch-extension-channels-list'>
      {sortByName(boards, (b) => b.name, (b) => b.merchOverlayId).map((board) => (
        <li
          key={board.merchOverlayId}
          data-testid='merch-extension-channel-row'
          className='flex items-baseline justify-between gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'
        >
          <span className='truncate text-sm text-dm-text-primary'>{board.name}</span>
          {board.channel === null
            ? (
              <span className='shrink-0 text-[11px] text-dm-text-secondary'>
                Event feed adapter&apos;s channel
              </span>
              )
            : (
              <span className='shrink-0 text-[11px] text-dm-text-primary'>
                <span className='font-mono'>{board.channel.login}</span>
                <span className='mx-1.5 text-dm-text-secondary'>·</span>
                <span className='font-mono text-dm-text-secondary'>{board.channel.channelId}</span>
              </span>
              )}
        </li>
      ))}
    </ul>
  )
}

export const MerchExtensionChannelsView = ({
  boards,
  loading,
  loadError,
  onRefresh
}: {
  boards: BoardChannel[] | null
  loading: boolean
  loadError: string | null
  onRefresh: () => void
}): JSX.Element => (
  <div
    data-testid='merch-extension-channels-card'
    className='mt-6 rounded-xl border border-white/10 bg-dm-bg-secondary p-6'
  >
    <div className='flex items-center justify-between gap-3'>
      <h3 className='text-base font-semibold text-dm-text-primary'>Merch extension channels</h3>
      <button
        onClick={onRefresh}
        disabled={loading}
        data-testid='merch-extension-channels-refresh'
        title='Re-read the merch boards'
        className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
      >
        <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
        Refresh
      </button>
    </div>
    <p className='mt-1.5 text-xs text-dm-text-secondary'>
      Which Twitch channel each merch board&apos;s live extension updates
      go to.
    </p>
    <ChannelsBody boards={boards} loadError={loadError} onRetry={onRefresh} />
  </div>
)

export const MerchExtensionChannelsCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const s = useMerchExtensionChannels(auth0Token)
  return (
    <MerchExtensionChannelsView
      boards={s.boards}
      loading={s.loading}
      loadError={s.loadError}
      onRefresh={() => { void s.load() }}
    />
  )
}
